import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import '../style.css';

const LEVELS = [
  { code: 'A1', name: 'Principiante', color: '#6b95f5', desc: 'Puedes presentarte, hacer preguntas simples y entender frases cotidianas.' },
  { code: 'A2', name: 'Básico', color: '#3b6fe0', desc: 'Te comunicas en situaciones rutinarias y describes tu entorno inmediato.' },
  { code: 'B1', name: 'Intermedio', color: '#f5c842', desc: 'Entiendes textos estándar y puedes desenvolverte en la mayoría de situaciones de viaje.' },
  { code: 'B2', name: 'Intermedio Alto', color: '#f5a623', desc: 'Te expresas con fluidez y puedes interactuar con hablantes nativos sin esfuerzo.' },
  { code: 'C1', name: 'Avanzado', color: '#e05c3b', desc: 'Usas el idioma con flexibilidad para fines académicos, profesionales y sociales.' },
  { code: 'C2', name: 'Maestría', color: '#1a1744', desc: 'Dominas el inglés con la misma precisión que un hablante nativo culto.' },
];

const FAQS = [
  {
    q: '¿Cuánto tiempo tarda el examen de ubicación?',
    a: 'Depende de tu nivel. La evaluación tiene 60 preguntas que aumentan en dificultad, pero no necesitas responderlas todas — cuando llegues a preguntas que no sabes, simplemente detienes el examen. La mayoría de personas termina en 10 a 20 minutos.',
  },
  {
    q: '¿Necesito crear una cuenta?',
    a: 'No necesitas crear una cuenta. Solo ingresa tu nombre, correo electrónico, teléfono y edad antes de comenzar. Esos datos nos permiten enviarte tu resultado y asignarte al grupo correcto.',
  },
  {
    q: '¿El examen incluye speaking?',
    a: 'Sí, dependiendo de tu puntaje en la parte escrita, el sistema puede solicitarte una breve prueba de speaking evaluada por inteligencia artificial. Te recomendamos hacer el examen en un lugar tranquilo por si aparece esta sección.',
  },
  {
    q: '¿Tengo que responder todas las preguntas?',
    a: 'No. El objetivo es conocer tu nivel real, no adivinar. Cuando llegues a preguntas que no puedes responder con seguridad, es mejor detener la evaluación en ese punto — así el resultado es más preciso.',
  },
  {
    q: '¿Qué tan confiable es el resultado?',
    a: 'El examen está alineado con el Marco Común Europeo de Referencia (MCER). Las preguntas aumentan progresivamente en dificultad y, en algunos casos, incluyen una prueba de speaking con IA para afinar el diagnóstico. El resultado refleja con precisión tus habilidades actuales.',
  },
  {
    q: '¿Qué hago después de conocer mi nivel?',
    a: 'Con tu nivel asignado, puedes explorar los cursos de Culturlingua diseñados para cada etapa o agendar una asesoría gratuita con nuestro equipo para encontrar el curso que mejor se adapte a tus objetivos y horarios.',
  },
  {
    q: '¿Puedo hacer el examen desde el celular?',
    a: 'Sí, el examen está optimizado para móviles, tabletas y computadoras. Si la prueba de speaking aparece, asegúrate de tener micrófono disponible y conexión estable.',
  },
];

function FaqItem({ q, a }) {
  const [open, setOpen] = useState(false);
  return (
    <div
      style={{ borderBottom: '1px solid #e5e7eb', padding: '20px 0', cursor: 'pointer' }}
      onClick={() => setOpen(!open)}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '16px' }}>
        <h3 style={{ fontSize: '1rem', fontWeight: '600', color: 'var(--navy)', margin: 0, fontFamily: 'DM Sans, sans-serif' }}>{q}</h3>
        <span style={{ fontSize: '1.25rem', color: 'var(--blue)', flexShrink: 0, transition: 'transform 0.2s', transform: open ? 'rotate(45deg)' : 'rotate(0)' }}>+</span>
      </div>
      {open && <p style={{ marginTop: '12px', color: 'var(--muted)', lineHeight: '1.7', fontSize: '0.95rem' }}>{a}</p>}
    </div>
  );
}

function ExamenUbicacion() {
  const location = useLocation();
  const fromBlog = new URLSearchParams(location.search).get('from') === 'blog';
  const examUTM = fromBlog
    ? '?utm_source=sitio&utm_medium=cta&utm_campaign=blog_post'
    : '?utm_source=sitio&utm_medium=pagina&utm_campaign=examen_ubicacion';
  const examHref = `https://linguahub.culturlingua.com/gral/placement_test/examen.php${examUTM}`;

  const jsonLd = {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'WebPage',
        '@id': 'https://culturlingua.com/Examen-de-ubicacion/',
        url: 'https://culturlingua.com/Examen-de-ubicacion/',
        name: 'Examen de Ubicación de Inglés Gratis | Culturlingua Tlaquepaque',
        description: 'Descubre tu nivel de inglés A1-C2 gratis. Evaluación con gramática, vocabulario y speaking con IA. Alineada al MCER. Para adultos, jóvenes y profesionales en Guadalajara y México.',
        inLanguage: 'es-MX',
        isPartOf: { '@id': 'https://culturlingua.com/' },
        breadcrumb: {
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: 'Inicio', item: 'https://culturlingua.com/' },
            { '@type': 'ListItem', position: 2, name: 'Inglés', item: 'https://culturlingua.com/Cursos' },
            { '@type': 'ListItem', position: 3, name: 'Examen de Ubicación', item: 'https://culturlingua.com/Examen-de-ubicacion/' },
          ],
        },
      },
      {
        '@type': 'EducationalOrganization',
        '@id': 'https://culturlingua.com/#org',
        name: 'Culturlingua',
        url: 'https://culturlingua.com/',
        logo: 'https://culturlingua.com/logo.webp',
        address: {
          '@type': 'PostalAddress',
          streetAddress: 'Reforma 31',
          addressLocality: 'Tlaquepaque',
          addressRegion: 'Jalisco',
          postalCode: '45500',
          addressCountry: 'MX',
        },
        telephone: '+52-33-3344-9139',
        sameAs: [
          'https://www.facebook.com/Culturlinguagdl',
          'https://www.instagram.com/culturlingua_gdl/',
        ],
      },
      {
        '@type': 'Quiz',
        name: 'Examen de Ubicación de Inglés — Culturlingua',
        description: 'Evaluación gratuita de nivel de inglés con 60 preguntas progresivas de gramática, vocabulario y comprensión, más una prueba de speaking con IA. Alineada al MCER (A1–C2).',
        url: examHref,
        educationalLevel: 'A1, A2, B1, B2, C1, C2',
        provider: { '@id': 'https://culturlingua.com/#org' },
        timeRequired: 'PT15M',
        isAccessibleForFree: true,
        inLanguage: 'es-MX',
        about: { '@type': 'Language', name: 'English', alternateName: 'en' },
      },
      {
        '@type': 'FAQPage',
        mainEntity: FAQS.map(({ q, a }) => ({
          '@type': 'Question',
          name: q,
          acceptedAnswer: { '@type': 'Answer', text: a },
        })),
      },
    ],
  };

  return (
    <div className="page-wrapper">
      <Helmet>
        <title>Examen de Ubicación de Inglés Gratis | Culturlingua Tlaquepaque</title>
        <meta
          name="description"
          content="Descubre tu nivel de inglés A1-C2 gratis. Evaluación con gramática, vocabulario y speaking con IA, alineada al MCER. Sin costo. Para adultos y jóvenes en Guadalajara, Jalisco y todo México."
        />
        <meta
          name="keywords"
          content="examen de ubicación de inglés, test de nivel inglés gratis, evaluar nivel de inglés, prueba de inglés online, nivel MCER inglés, examen inglés Guadalajara, test inglés Tlaquepaque, diagnóstico inglés México, speaking inglés IA"
        />
        <link rel="canonical" href="https://culturlingua.com/Examen-de-ubicacion/" />

        <meta property="og:type" content="website" />
        <meta property="og:title" content="Examen de Ubicación de Inglés Gratis | Culturlingua" />
        <meta property="og:description" content="Descubre tu nivel de inglés A1-C2 gratis. Incluye gramática, vocabulario y speaking con IA. Sin costo. Resultado inmediato." />
        <meta property="og:url" content="https://culturlingua.com/Examen-de-ubicacion/" />
        <meta property="og:image" content="https://culturlingua.com/logo.webp" />
        <meta property="og:locale" content="es_MX" />
        <meta property="og:site_name" content="Culturlingua" />

        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="Examen de Ubicación de Inglés Gratis | Culturlingua" />
        <meta name="twitter:description" content="Descubre tu nivel de inglés A1-C2 gratis. Incluye speaking con IA. Sin costo. Resultado inmediato." />
        <meta name="twitter:image" content="https://culturlingua.com/logo.webp" />

        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      {/* ── HERO ── */}
      <div className="page-hero" style={{ paddingBottom: '80px' }}>
        <div className="section-tag" style={{ marginBottom: '16px' }}>Gratis · Alineado al MCER · Speaking con IA</div>
        <h1>Examen de Ubicación de Inglés</h1>
        <p style={{ maxWidth: '640px', margin: '16px auto 0' }}>
          Descubre tu nivel real de inglés — de A1 a C2 — con nuestra evaluación gratuita.
          Incluye gramática, vocabulario y, según tu puntaje, una prueba de speaking evaluada por inteligencia artificial.
        </p>
        <div style={{ display: 'flex', gap: '12px', flexWrap: 'wrap', justifyContent: 'center', marginTop: '32px' }}>
          {['🆓 Completamente gratis', '📝 60 preguntas progresivas', '🎤 Speaking con IA', '🏆 Resultado inmediato', '🎓 Alineado al MCER'].map(p => (
            <span key={p} style={{ background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.18)', padding: '6px 16px', borderRadius: '100px', fontSize: '0.85rem', color: 'rgba(255,255,255,0.9)', fontWeight: '500' }}>
              {p}
            </span>
          ))}
        </div>
      </div>

      {/* ── CTA PRINCIPAL ── */}
      <section style={{ background: 'var(--cream)', padding: '60px 24px' }}>
        <div style={{ maxWidth: '820px', margin: '0 auto' }}>

          {/* Card principal */}
          <div style={{ background: 'white', border: '1px solid #e5e7eb', borderRadius: '16px', padding: '32px', marginBottom: '12px' }}>
            <p style={{ fontSize: '12px', color: 'var(--muted)', marginBottom: '6px', textTransform: 'uppercase', letterSpacing: '0.08em' }}>Evaluación gratuita</p>
            <h2 style={{ fontSize: '1.35rem', fontWeight: '700', color: 'var(--navy)', marginBottom: '10px', lineHeight: '1.3', fontFamily: 'Fraunces, serif' }}>
              ¿En qué nivel de inglés estás?
            </h2>
            <p style={{ fontSize: '0.9rem', color: 'var(--muted)', lineHeight: '1.7', marginBottom: '20px' }}>
              Responde hasta donde puedas — el examen para automáticamente cuando llegas a tu límite. No adivines: el resultado es más preciso si te detienes cuando ya no sabes.
            </p>

            {/* Chips de habilidades */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginBottom: '24px' }}>
              {[
                {
                  label: 'Gramática y vocabulario',
                  sub: null,
                  bg: '#e8f0ff',
                  icon: (
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <path d="M2 4h12M2 8h8M2 12h10" stroke="#3b6fe0" strokeWidth="1.5" strokeLinecap="round"/>
                    </svg>
                  ),
                },
                {
                  label: 'Comprensión lectora',
                  sub: null,
                  bg: '#e8f5ee',
                  icon: (
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <path d="M8 2a6 6 0 100 12A6 6 0 008 2z" stroke="#1D9E75" strokeWidth="1.5"/>
                      <path d="M5.5 8l1.5 1.5L10.5 6" stroke="#1D9E75" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                  ),
                },
                {
                  label: 'Speaking con IA',
                  sub: 'según tu puntaje',
                  bg: '#fef3e2',
                  icon: (
                    <svg width="14" height="14" viewBox="0 0 16 16" fill="none">
                      <circle cx="8" cy="5" r="2.5" stroke="#BA7517" strokeWidth="1.5"/>
                      <path d="M4 13c0-2.2 1.8-4 4-4s4 1.8 4 4" stroke="#BA7517" strokeWidth="1.5" strokeLinecap="round"/>
                      <path d="M11 8.5c.8.5 1.5 1.4 1.5 2.5" stroke="#BA7517" strokeWidth="1.2" strokeLinecap="round"/>
                    </svg>
                  ),
                },
              ].map(({ label, sub, bg, icon }) => (
                <div key={label} style={{ display: 'flex', alignItems: 'center', gap: '8px', background: 'var(--cream)', border: '1px solid #e5e7eb', borderRadius: '8px', padding: '8px 14px' }}>
                  <div style={{ width: '28px', height: '28px', borderRadius: '6px', background: bg, display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    {icon}
                  </div>
                  <div>
                    <div style={{ fontSize: '13px', color: 'var(--text)' }}>{label}</div>
                    {sub && <div style={{ fontSize: '11px', color: 'var(--muted)' }}>{sub}</div>}
                  </div>
                </div>
              ))}
            </div>

            {/* Botón */}
            <a
              href={examHref}
              target="_blank"
              rel="noopener noreferrer"
              className="btn-glow"
              style={{ display: 'block', textAlign: 'center', fontSize: '1rem', padding: '15px 24px', borderRadius: '10px' }}
              aria-label="Hacer el examen de ubicación de inglés gratis en Culturlingua"
            >
              Comenzar examen →
            </a>

            {/* Meta info */}
            <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '12px' }}>
              {[
                { label: '10–20 min' },
                { label: 'Gratis' },
                { label: 'Lugar tranquilo recomendado' },
              ].map(({ label }, i, arr) => (
                <React.Fragment key={label}>
                  <span style={{ fontSize: '12px', color: 'var(--muted)', display: 'flex', alignItems: 'center', gap: '4px' }}>{label}</span>
                  {i < arr.length - 1 && <span style={{ width: '4px', height: '4px', borderRadius: '50%', background: '#d1d5db', alignSelf: 'center', display: 'inline-block' }} />}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Tip */}
          <div style={{ background: 'rgba(59,111,224,0.04)', borderLeft: '2px solid var(--blue)', borderRadius: '0 8px 8px 0', padding: '12px 16px', display: 'flex', gap: '10px', alignItems: 'flex-start' }}>
            <div style={{ width: '18px', height: '18px', borderRadius: '50%', background: 'var(--blue)', flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', marginTop: '1px' }}>
              <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                <path d="M6 2v4M6 8.5v1" stroke="white" strokeWidth="1.5" strokeLinecap="round"/>
              </svg>
            </div>
            <p style={{ margin: 0, fontSize: '0.85rem', color: 'var(--text)', lineHeight: '1.6' }}>
              <strong>No adivines.</strong> El examen está diseñado para parar donde termina tu nivel real — así te asignamos al grupo correcto desde el primer día.
            </p>
          </div>

        </div>
      </section>

      {/* ── POR QUÉ HACER EL EXAMEN ── */}
      <section style={{ padding: '72px 24px', background: 'white' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <div className="section-tag" style={{ marginBottom: '12px' }}>¿Para qué sirve?</div>
            <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', color: 'var(--navy)', fontWeight: '900' }}>
              Por qué hacer el examen de ubicación
            </h2>
            <p style={{ color: 'var(--muted)', marginTop: '12px', maxWidth: '560px', margin: '12px auto 0', lineHeight: '1.7' }}>
              Conocer tu nivel real es el primer paso para aprender inglés de forma eficiente.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '24px' }}>
            {[
              { icon: '🎯', title: 'Entra al grupo correcto', desc: 'Evita aburrirte en clases demasiado fáciles o frustrarte en niveles que aún no son para ti. El grupo correcto acelera tu aprendizaje.' },
              { icon: '💼', title: 'Certifica tu nivel para trabajo o estudio', desc: 'Muchas empresas y universidades piden evidencia de nivel de inglés. El examen te da un punto de referencia oficial alineado al MCER.' },
              { icon: '📈', title: 'Mide tu progreso', desc: 'Si ya estudiaste inglés antes, el examen te muestra exactamente dónde quedaste y cuánto te falta para alcanzar tu meta.' },
              { icon: '🌎', title: 'Prepárate para certificaciones', desc: 'Identifica tu nivel actual antes de inscribirte a TOEFL, IELTS o Cambridge. Sabrás cuánto tiempo de preparación necesitas.' },
            ].map(({ icon, title, desc }) => (
              <div key={title} style={{ background: 'var(--cream)', borderRadius: '16px', padding: '28px', border: '1px solid #e5e7eb' }}>
                <div style={{ fontSize: '2rem', marginBottom: '12px' }}>{icon}</div>
                <h3 style={{ fontSize: '1.05rem', fontWeight: '700', color: 'var(--navy)', marginBottom: '8px' }}>{title}</h3>
                <p style={{ color: 'var(--muted)', fontSize: '0.9rem', lineHeight: '1.6' }}>{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── NIVELES MCER ── */}
      <section style={{ padding: '72px 24px', background: 'var(--navy)' }}>
        <div style={{ maxWidth: '1000px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <div className="section-tag" style={{ background: 'rgba(245,200,66,0.15)', color: 'var(--gold)', borderColor: 'rgba(245,200,66,0.3)', marginBottom: '12px' }}>
              Marco Común Europeo de Referencia
            </div>
            <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', color: 'white', fontWeight: '900' }}>
              ¿Qué nivel de inglés tienes?
            </h2>
            <p style={{ color: 'rgba(255,255,255,0.6)', marginTop: '12px', maxWidth: '500px', margin: '12px auto 0', lineHeight: '1.7' }}>
              El MCER define 6 niveles internacionales de dominio del idioma. El examen identifica en cuál estás.
            </p>
          </div>
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
            {LEVELS.map(({ code, name, color, desc }) => (
              <div key={code} style={{ background: 'rgba(255,255,255,0.05)', border: '1px solid rgba(255,255,255,0.08)', borderRadius: '16px', padding: '24px', display: 'flex', gap: '16px', alignItems: 'flex-start' }}>
                <span style={{ background: color, color: 'white', fontWeight: '800', fontSize: '0.85rem', padding: '4px 10px', borderRadius: '8px', flexShrink: 0, marginTop: '2px', fontFamily: 'DM Sans, sans-serif' }}>
                  {code}
                </span>
                <div>
                  <h3 style={{ color: 'white', fontWeight: '700', fontSize: '1rem', marginBottom: '6px' }}>{name}</h3>
                  <p style={{ color: 'rgba(255,255,255,0.55)', fontSize: '0.85rem', lineHeight: '1.6' }}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CÓMO FUNCIONA ── */}
      <section style={{ padding: '72px 24px', background: 'white' }}>
        <div style={{ maxWidth: '800px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <div className="section-tag" style={{ marginBottom: '12px' }}>Proceso</div>
            <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', color: 'var(--navy)', fontWeight: '900' }}>
              ¿Cómo funciona el examen?
            </h2>
          </div>
          <div style={{ display: 'flex', flexDirection: 'column' }}>
            {[
              { step: '1', title: 'Ingresa tus datos', desc: 'Proporciona tu nombre, correo, teléfono y edad. Eso nos permite enviarte tu resultado y asignarte al grupo correcto.' },
              { step: '2', title: 'Responde las preguntas', desc: '60 preguntas de gramática, vocabulario y comprensión lectora que aumentan progresivamente en dificultad. Para cuando ya no sepas una respuesta — no adivines.' },
              { step: '3', title: 'Prueba de speaking (si aplica)', desc: 'Dependiendo de tu puntaje escrito, el sistema puede pedirte una breve prueba de speaking evaluada por inteligencia artificial. Por eso recomendamos un lugar tranquilo.' },
              { step: '4', title: 'Recibe tu nivel y recomendación', desc: 'Obtienes tu nivel A1-C2 con una descripción de tus habilidades y el curso de Culturlingua recomendado para ti.' },
            ].map(({ step, title, desc }, i, arr) => (
              <div key={step} style={{ display: 'flex', gap: '24px', paddingBottom: i < arr.length - 1 ? '32px' : '0', position: 'relative' }}>
                {i < arr.length - 1 && (
                  <div style={{ position: 'absolute', left: '19px', top: '44px', bottom: 0, width: '2px', background: '#e5e7eb' }} />
                )}
                <div style={{ width: '40px', height: '40px', borderRadius: '50%', background: 'var(--blue)', color: 'white', fontWeight: '800', fontSize: '0.95rem', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, zIndex: 1, fontFamily: 'DM Sans, sans-serif' }}>
                  {step}
                </div>
                <div style={{ paddingTop: '8px' }}>
                  <h3 style={{ fontSize: '1.05rem', fontWeight: '700', color: 'var(--navy)', marginBottom: '6px' }}>{title}</h3>
                  <p style={{ color: 'var(--muted)', fontSize: '0.9rem', lineHeight: '1.6' }}>{desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FAQ ── */}
      <section style={{ padding: '72px 24px', background: 'var(--cream)' }}>
        <div style={{ maxWidth: '760px', margin: '0 auto' }}>
          <div style={{ textAlign: 'center', marginBottom: '48px' }}>
            <div className="section-tag" style={{ marginBottom: '12px' }}>Preguntas frecuentes</div>
            <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', color: 'var(--navy)', fontWeight: '900' }}>
              Todo lo que necesitas saber
            </h2>
          </div>
          <div>
            {FAQS.map(({ q, a }) => (
              <FaqItem key={q} q={q} a={a} />
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA FINAL ── */}
      <section style={{ padding: '72px 24px', background: 'var(--navy)', textAlign: 'center' }}>
        <div style={{ maxWidth: '600px', margin: '0 auto' }}>
          <h2 style={{ fontFamily: 'Fraunces, serif', fontSize: 'clamp(1.75rem, 3vw, 2.5rem)', color: 'white', fontWeight: '900', marginBottom: '16px' }}>
            ¿Listo para descubrir tu nivel?
          </h2>
          <p style={{ color: 'rgba(255,255,255,0.65)', marginBottom: '32px', lineHeight: '1.7' }}>
            La evaluación es gratuita, tarda entre 10 y 20 minutos, y te da un diagnóstico completo: escritura y speaking. Busca un lugar tranquilo y comienza cuando quieras.
          </p>
          <a
            href={examHref}
            target="_blank"
            rel="noopener noreferrer"
            className="btn-glow"
            style={{ display: 'inline-block', fontSize: '1.1rem', padding: '18px 40px', borderRadius: '12px' }}
            aria-label="Empezar el examen de ubicación de inglés gratis"
          >
            Empezar el Examen Ahora →
          </a>
          <div style={{ display: 'flex', gap: '16px', justifyContent: 'center', flexWrap: 'wrap', marginTop: '32px' }}>
            <Link to="/Cursos" className="btn-outline" style={{ color: 'rgba(255,255,255,0.8)', border: '1.5px solid rgba(255,255,255,0.25)' }}>
              Ver cursos de inglés
            </Link>
            <Link to="/contacto" className="btn-outline" style={{ color: 'rgba(255,255,255,0.8)', border: '1.5px solid rgba(255,255,255,0.25)' }}>
              Hablar con un asesor
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default ExamenUbicacion;